import type { LocalInitializationConsent } from '@pokerroom/types';
/** Tab-local recovery evidence only; never financial authority. */
export type CashIntent = {
  recordVersion: 1;
  scope: string;
  createdAt: number;
  lastSeenAt: number;
  action:
    | 'OPEN_SESSION'
    | 'ISSUE_DEPOSIT_SOURCE'
    | 'CASH_DEPOSIT'
    | 'CASH_HANDOFF'
    | 'CASH_ZIPA_REDEMPTION'
    | 'CASH_PETTY_TRANSACTION'
    | 'CASH_PETTY_EXTERNAL_TOPUP'
    | 'REVERSE_PETTY_EXTERNAL_TOPUP'
    | 'CASH_REPLENISH'
    | 'CASH_REVENUE_HANDOFF'
    | 'UPGRADE_CASH_CONTRACT'
    | 'CLOSE_LOCATION_SESSION';
  operationKey: string;
  issuanceKey?: string;
  registrationVersion?: 1;
  localBusinessProtocol?: 1;
  localInitializationConsent?: LocalInitializationConsent;
  localInitializationOutcome?: 'KNOWN_REJECTED';
  sourceId?: string;
  sourceRef?: string;
  sessionId?: string;
  businessDate?: string;
  openingRef?: string;
  body?: Record<string, unknown>;
  stage: 'UNKNOWN' | 'REGISTERED' | 'PREPARED' | 'COMMITTED' | 'REJECTED' | 'VOIDED';
};
export function retainCashIntent(
  record: CashIntent,
  scope: string,
  now: number,
): CashIntent | null {
  if (
    record.recordVersion !== 1 ||
    record.scope !== scope ||
    !record.operationKey ||
    !Number.isFinite(record.createdAt) ||
    !Number.isFinite(record.lastSeenAt)
  )
    return null;
  const age = now - record.createdAt;
  if (age >= 24 * 3600000) return null;
  const { body, localInitializationConsent, ...refs } = record;
  if (
    age < 0 ||
    now < record.lastSeenAt ||
    age >= 8 * 3600000 ||
    ['COMMITTED', 'VOIDED'].includes(record.stage)
  )
    return { ...refs, lastSeenAt: Math.max(now, record.lastSeenAt) };
  return { ...refs, body, localInitializationConsent, lastSeenAt: now };
}
export function resolveCashMode(
  context: unknown,
  venueId: string,
): 'UNKNOWN' | 'LEGACY_V1' | 'LOCATION_V1' {
  const c = context as Record<string, unknown> | undefined;
  return c?.contractVersion === 1 &&
    venueId &&
    c.venueId === venueId &&
    typeof c.currentBusinessDate === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(c.currentBusinessDate) &&
    typeof c.serverNow === 'string' &&
    Number.isFinite(Date.parse(c.serverNow)) &&
    typeof c.sourceWatermark === 'string' &&
    !!c.sourceWatermark &&
    Array.isArray(c.guardReasons) &&
    c.guardReasons.every((x) => typeof x === 'string') &&
    ['CURRENT_OPEN', 'NO_CURRENT_OPEN', 'HISTORICAL_OPEN'].includes(String(c.sessionState)) &&
    (c.sessionState === 'NO_CURRENT_OPEN'
      ? c.activeSessionId === null && c.activeSessionBusinessDate === null
      : typeof c.activeSessionId === 'string' &&
        !!c.activeSessionId &&
        typeof c.activeSessionBusinessDate === 'string') &&
    (c.cashMode === 'LEGACY_V1' || c.cashMode === 'LOCATION_V1')
    ? c.cashMode
    : 'UNKNOWN';
}
export function validateDeposit(
  value: unknown,
  expected: { sourceId?: string; sessionId?: string; businessDate?: string },
): boolean {
  const p = value as Record<string, unknown> | null;
  return (
    !!p &&
    p.contractVersion === 1 &&
    p.status === 'COMMITTED' &&
    [
      'operationId',
      'originalOperationKey',
      'cashMovementId',
      'walletLogId',
      'cashSourceId',
      'sessionId',
      'businessDate',
      'appliedBalance',
    ].every((k) => typeof p[k] === 'string' && p[k] !== '') &&
    /^\d+$/.test(String(p.walletLogId)) &&
    /^-?\d+\.\d{2}$/.test(String(p.appliedBalance)) &&
    p.balance === p.appliedBalance &&
    typeof p.replayed === 'boolean' &&
    (!expected.sourceId || p.cashSourceId === expected.sourceId) &&
    (!expected.sessionId || p.sessionId === expected.sessionId) &&
    (!expected.businessDate || p.businessDate === expected.businessDate)
  );
}
export function cashFailure(
  error: unknown,
):
  | 'UNKNOWN'
  | 'RECONCILE'
  | 'REFRESH'
  | 'DENIED'
  | 'SCOPE_REQUIRED'
  | 'EXPIRED'
  | 'SCOPE_MISMATCH'
  | 'VOIDED'
  | 'ALREADY_PREPARED' {
  const e = error as { response?: { status?: number; data?: { errorCode?: string } } };
  if (e?.response?.status === 401 || e?.response?.status === 403) return 'DENIED';
  const code = e?.response?.data?.errorCode;
  if (code === 'RECOVERY_SCOPE_REQUIRED') return 'SCOPE_REQUIRED';
  if (code === 'RECOVERY_EXPIRED') return 'EXPIRED';
  if (code === 'RECOVERY_SCOPE_MISMATCH') return 'SCOPE_MISMATCH';
  if (code === 'INTENT_VOIDED') return 'VOIDED';
  if (code === 'SOURCE_ALREADY_PREPARED') return 'ALREADY_PREPARED';
  if (code === 'STALE_VERSION' || code === 'DAILY_SETTLEMENT_PROTOCOL_REQUIRED') return 'REFRESH';
  if (
    [
      'KEY_REUSED',
      'SOURCE_CONFLICT',
      'SOURCE_UNRESOLVED',
      'RECONCILIATION_REQUIRED',
      'SESSION_NOT_OPEN',
      'RETRY_REQUIRED',
    ].includes(code ?? '')
  )
    return 'RECONCILE';
  return 'UNKNOWN';
}

/** Keep original member operation identity, never its replay payload, after scope loss. */
export function memberCashReference(value: unknown, now: number): CashIntent | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as CashIntent;
  if (
    !['ISSUE_DEPOSIT_SOURCE', 'CASH_DEPOSIT', 'CASH_ZIPA_REDEMPTION'].includes(record.action) ||
    typeof record.scope !== 'string'
  )
    return null;
  const retained = retainCashIntent(record, record.scope, now);
  if (!retained) return null;
  const reference = { ...retained };
  delete reference.body;
  delete reference.localInitializationConsent;
  return reference;
}
